import React, { useState, useEffect } from 'react';
import ImageGenerator from './ImageGenerator';
import './BubbleForm.css';

const BubbleForm = ({ bubble, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    thought: '',
    author: '',
    application: '',
    summary: '',
    imageUrl: '',
    url: ''
  });
  const [errors, setErrors] = useState({});
  const [showImageGenerator, setShowImageGenerator] = useState(false);

  useEffect(() => {
    if (bubble) {
      setFormData({
        thought: bubble.thought || bubble.text || '',
        author: bubble.author || '',
        application: bubble.application || '',
        summary: bubble.summary || '',
        url: bubble.url || bubble.youtubeLink || ''
      });
    }
  }, [bubble]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.thought.trim()) {
      newErrors.thought = 'Thought is required';
    }
    
    if (!formData.author.trim()) {
      newErrors.author = 'Author is required';
    }
    
    if (!formData.application.trim()) {
      newErrors.application = 'Application is required';
    }
    
    if (!formData.url.trim()) {
      newErrors.url = 'URL is required';
    } else if (!isValidUrl(formData.url)) {
      newErrors.url = 'Please enter a valid URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleImageGenerated = (imageUrl) => {
    setFormData(prev => ({
      ...prev,
      imageUrl: imageUrl
    }));
    setShowImageGenerator(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="bubble-form-overlay">
      <div className="bubble-form-container">
        <div className="bubble-form-header">
          <h2>{bubble ? 'Edit Bubble' : 'Add New Bubble'}</h2>
          <button onClick={onCancel} className="close-btn">×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="bubble-form">
          <div className="form-group">
            <label htmlFor="thought">Thought *</label>
            <input
              type="text"
              id="thought"
              name="thought"
              value={formData.thought}
              onChange={handleChange}
              placeholder="Enter the main thought text"
              className={errors.thought ? 'error' : ''}
            />
            {errors.thought && <span className="error-text">{errors.thought}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="author">Author *</label>
            <input
              type="text"
              id="author"
              name="author"
              value={formData.author}
              onChange={handleChange}
              placeholder="Enter the author name"
              className={errors.author ? 'error' : ''}
            />
            {errors.author && <span className="error-text">{errors.author}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="application">Application *</label>
            <textarea
              id="application"
              name="application"
              value={formData.application}
              onChange={handleChange}
              placeholder="Describe how this thought can be applied"
              rows="3"
              className={errors.application ? 'error' : ''}
            />
            {errors.application && <span className="error-text">{errors.application}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="summary">Summary</label>
            <textarea
              id="summary"
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              placeholder="Enter a detailed summary or explanation"
              rows="4"
            />
          </div>

          <div className="form-group">
            <label htmlFor="url">URL *</label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="https://www.youtube.com/watch?v=..."
              className={errors.url ? 'error' : ''}
            />
            {errors.url && <span className="error-text">{errors.url}</span>}
          </div>

          <div className="form-group">
            <label>AI Generated Image</label>
            <div className="image-generation-section">
              {formData.imageUrl ? (
                <div className="image-preview">
                  <img 
                    src={formData.imageUrl} 
                    alt="Generated thought"
                    className="preview-image"
                  />
                  <button 
                    type="button" 
                    onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                    className="remove-image-btn"
                  >
                    Remove Image
                  </button>
                </div>
              ) : (
                <div className="no-image">
                  <p>No image generated yet</p>
                </div>
              )}
              <button 
                type="button" 
                onClick={() => setShowImageGenerator(true)}
                className="generate-image-btn"
                disabled={!formData.thought || !formData.author}
              >
                🎨 Generate AI Image
              </button>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              {bubble ? 'Update Bubble' : 'Add Bubble'}
            </button>
          </div>
        </form>
      </div>

      {showImageGenerator && (
        <ImageGenerator
          thought={formData.thought}
          author={formData.author}
          onImageGenerated={handleImageGenerated}
          onClose={() => setShowImageGenerator(false)}
        />
      )}
    </div>
  );
};

export default BubbleForm;
